import type {
  AccountUser,
  AppConfig,
  BoardListFilter,
  BoardSharing,
  BoardSummary,
  BoardVersionInfo,
  LinkAccess,
  SpaceSharing,
  SpaceSummary,
  TemplateId,
  UploadedAsset,
} from '@doriavboard/shared';
import { api } from './client';

export const authApi = {
  config: () => api.get<AppConfig>('/api/config'),
  me: () => api.get<{ user: AccountUser }>('/api/auth/me'),
  login: (username: string, password: string) =>
    api.post<{ user: AccountUser }>('/api/auth/login', { username, password }),
  register: (input: { username: string; name: string; password: string }) =>
    api.post<{ user: AccountUser }>('/api/auth/register', input),
  guest: (name: string) => api.post<{ user: AccountUser }>('/api/auth/guest', { name }),
  claim: (username: string, password: string) =>
    api.post<{ user: AccountUser }>('/api/auth/claim', { username, password }),
  logout: () => api.post<void>('/api/auth/logout'),
  updateProfile: (input: { name?: string; color?: string }) =>
    api.patch<{ user: AccountUser }>('/api/auth/me', input),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/api/auth/password', { currentPassword, newPassword }),
  serverTime: () => api.get<{ now: number }>('/api/time'),
};

const board = (id: string) => `/api/boards/${encodeURIComponent(id)}`;

export const boardsApi = {
  list: (filter: BoardListFilter) =>
    api.get<{ boards: BoardSummary[] }>(`/api/boards?filter=${filter}`),
  create: (input: { title?: string; template?: TemplateId }) =>
    api.post<{ board: BoardSummary }>('/api/boards', input),
  get: (id: string) => api.get<{ board: BoardSummary }>(board(id)),
  update: (
    id: string,
    input: { title?: string; linkAccess?: LinkAccess; spaceId?: string | null },
  ) => api.patch<{ board: BoardSummary }>(board(id), input),
  remove: (id: string) => api.delete(board(id)),
  duplicate: (id: string, title?: string) =>
    api.post<{ board: BoardSummary }>(`${board(id)}/duplicate`, title ? { title } : {}),
  exportUrl: (id: string) => `${board(id)}/export`,
  /** Imports a board file, into a space when `spaceId` is given. */
  import: (file: File, spaceId?: string) => {
    const form = new FormData();
    form.append('file', file);
    const query = spaceId ? `?spaceId=${encodeURIComponent(spaceId)}` : '';
    return api.post<{ board: BoardSummary }>(`/api/boards/import${query}`, form);
  },

  sharing: (id: string) => api.get<BoardSharing>(`${board(id)}/members`),
  invite: (id: string, username: string, role: 'editor' | 'viewer') =>
    api.post<BoardSharing>(`${board(id)}/members`, { username, role }),
  setMemberRole: (id: string, userId: string, role: 'editor' | 'viewer') =>
    api.patch<BoardSharing>(`${board(id)}/members/${userId}`, { role }),
  removeMember: (id: string, userId: string) => api.delete(`${board(id)}/members/${userId}`),

  uploadAsset: (id: string, file: Blob, filename = 'image') => {
    const form = new FormData();
    form.append('file', file, filename);
    return api.post<UploadedAsset>(`${board(id)}/assets`, form);
  },
  copyAssets: (id: string, fromBoardId: string, assetIds: string[]) =>
    api.post<{ assets: Record<string, UploadedAsset> }>(`${board(id)}/assets/copy`, {
      fromBoardId,
      assetIds,
    }),
  assetUrl: (id: string, assetId: string) => `${board(id)}/assets/${encodeURIComponent(assetId)}`,
  uploadThumbnail: (id: string, image: Blob) => api.put<void>(`${board(id)}/thumbnail`, image),

  versions: (id: string) => api.get<{ versions: BoardVersionInfo[] }>(`${board(id)}/versions`),
  saveVersion: (id: string, label?: string) =>
    api.post<{ version: { id: string } }>(`${board(id)}/versions`, label ? { label } : {}),
  versionContent: async (id: string, versionId: string) => {
    const res = await fetch(`${board(id)}/versions/${versionId}/content`, {
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('Could not load that version');
    return new Uint8Array(await res.arrayBuffer());
  },
  restoreVersion: (id: string, versionId: string) =>
    api.post<{ ok: true }>(`${board(id)}/versions/${versionId}/restore`),
};

const space = (id: string) => `/api/spaces/${encodeURIComponent(id)}`;

export const spacesApi = {
  list: () => api.get<{ spaces: SpaceSummary[] }>('/api/spaces'),
  create: (name: string) => api.post<{ space: SpaceSummary }>('/api/spaces', { name }),
  get: (id: string) => api.get<{ space: SpaceSummary }>(space(id)),
  rename: (id: string, name: string) => api.patch<{ space: SpaceSummary }>(space(id), { name }),
  remove: (id: string) => api.delete(space(id)),

  boards: (id: string) => api.get<{ boards: BoardSummary[] }>(`${space(id)}/boards`),
  createBoard: (id: string, input: { title?: string; template?: TemplateId }) =>
    api.post<{ board: BoardSummary }>(`${space(id)}/boards`, input),

  sharing: (id: string) => api.get<SpaceSharing>(`${space(id)}/members`),
  invite: (id: string, username: string, role: 'editor' | 'viewer') =>
    api.post<SpaceSharing>(`${space(id)}/members`, { username, role }),
  setMemberRole: (id: string, userId: string, role: 'editor' | 'viewer') =>
    api.patch<SpaceSharing>(`${space(id)}/members/${userId}`, { role }),
  removeMember: (id: string, userId: string) => api.delete(`${space(id)}/members/${userId}`),
};
